import { supabase } from '../supabaseClient';

const RHINO_CHECK_KEY = 'driver_score_last_rhino_check';
const RHINO_WEEK_KEY = 'driver_score_rhino_week';

interface RhinoEvent {
    date: string;
    description: string;
    score: number;
    type: 'multa' | 'comportamento' | 'aviso' | 'elogio' | 'regular';
}

interface RhinoEmailData {
    score: number;
    tier: 'Ouro' | 'Prata' | 'Bronze';
    events: RhinoEvent[];
    lastUpdate: string;
}

// Parse the event table from Rhino email
// Format: Data | Evento | Saldo após evento
const parseRhinoEmail = (decodedBody: string): RhinoEmailData | null => {
    const lines = decodedBody.split('\n');
    const events: RhinoEvent[] = [];
    
    // Extract tier from line like "grupo 1 - OURO" or "grupo X - PRATA/BRONZE"
    let tier: RhinoEmailData['tier'] = 'Bronze';
    const tierMatch = decodedBody.match(/grupo\s*\d*\s*-\s*(OURO|PRATA|BRONZE)/i);
    if (tierMatch) {
        const found = tierMatch[1].toUpperCase();
        if (found === 'OURO') tier = 'Ouro';
        else if (found === 'PRATA') tier = 'Prata';
        else tier = 'Bronze';
    }
    
    // Score is in the last line with number
    let lastScore = 100;
    
    // Regex to match event lines: YYYY-MM-DD  [Type] description  score
    const eventRegex = /^(\d{4}-\d{2}-\d{2})\s*\[([^\]]+)\]\s*(.+?)\s*(\d+)\s*$/;
    
    for (const line of lines) {
        const match = line.trim().match(eventRegex);
        if (match) {
            const [, date, type_raw, description, scoreStr] = match;
            const score = parseInt(scoreStr, 10);
            const type_raw_lower = type_raw.toLowerCase();
            
            // Determine event type
            let type: RhinoEvent['type'] = 'regular';
            if (type_raw_lower.includes('multa') || type_raw_lower.includes('penalidade')) {
                type = 'multa';
            } else if (type_raw_lower.includes('comportamento') || type_raw_lower.includes('atraso') || type_raw_lower.includes('demorou')) {
                type = 'comportamento';
            } else if (type_raw_lower.includes('aviso') || type_raw_lower.includes('advertência')) {
                type = 'aviso';
            } else if (type_raw_lower.includes('elogio') || type_raw_lower.includes('bônus') || type_raw_lower.includes('parabéns')) {
                type = 'elogio';
            }
            
            events.push({
                date,
                description: description.trim(),
                score,
                type
            });
            
            lastScore = score;
        }
    }
    
    if (events.length === 0) {
        // Try alternative: look for "Saldo" or "pontos" pattern
        const scoreMatch = decodedBody.match(/(\d+)\s*pontos?/m);
        if (scoreMatch) {
            lastScore = parseInt(scoreMatch[1], 10);
            // Infer tier from score if not found
            if (lastScore >= 95) tier = 'Ouro';
            else if (lastScore >= 80) tier = 'Prata';
        } else {
            console.log('RhinoService: Could not parse any events from email.');
            return null;
        }
    }
    
    // Sort by date descending (most recent first)
    events.sort((a, b) => b.date.localeCompare(a.date));
    
    return {
        score: lastScore,
        tier,
        events: events.slice(0, 10),
        lastUpdate: events[0]?.date || new Date().toISOString()
    };
};

// Fetch Rhino email data using Gmail API
const fetchRealRhinoEmailData = async (providerToken: string | null): Promise<RhinoEmailData | null> => {
    if (!providerToken) {
        console.warn('RhinoService: Missing Google provider token.');
        return null;
    }

    try {
        // Search for emails from Rhino (try both senders and keywords)
        const queries = [
            'drivers-notification newer_than:7d',
            'drivers-notifications newer_than:7d',
            'from:drivers-notifications@go-rhino.com newer_than:7d',
            'from:drivers-notification@go-rhino.com newer_than:7d',
            'subject:drivers-notification newer_than:7d',
            'from:rhino newer_than:7d'
        ];
        
        let emailData: RhinoEmailData | null = null;
        
        for (const query of queries) {
            const searchRes = await fetch(
                `https://gmail.googleapis.com/gmail/v1/users/me/messages?q=${encodeURIComponent(query)}&maxResults=1`,
                { headers: { Authorization: `Bearer ${providerToken}` } }
            );
            
            if (!searchRes.ok) continue;
            
            const searchData = await searchRes.json();
            if (!searchData.messages?.length) continue;
            
            // Get message content
            const msgRes = await fetch(
                `https://gmail.googleapis.com/gmail/v1/users/me/messages/${searchData.messages[0].id}`,
                { headers: { Authorization: `Bearer ${providerToken}` } }
            );
            
            if (!msgRes.ok) continue;
            
            const msgData = await msgRes.json();
            
            // Extract body
            let emailBodyBase64 = '';
            if (msgData.payload?.parts?.length) {
                const part = msgData.payload.parts.find((p: any) => p.mimeType === 'text/plain') || msgData.payload.parts[0];
                if (part?.body?.data) emailBodyBase64 = part.body.data;
            } else if (msgData.payload?.body?.data) {
                emailBodyBase64 = msgData.payload.body.data;
            }
            
            if (!emailBodyBase64) continue;
            
            // Decode base64
            const base64 = emailBodyBase64.replace(/-/g, '+').replace(/_/g, '/');
            let decodedBody = '';
            try {
                decodedBody = decodeURIComponent(escape(atob(base64)));
            } catch {
                decodedBody = atob(base64);
            }
            
            // Parse
            emailData = parseRhinoEmail(decodedBody);
            if (emailData) break;
        }
        
        return emailData;
    } catch (err) {
        console.error('RhinoService: Error reading email', err);
    }
    return null;
};

export const RhinoService = {

    async checkAndProcess(userId: string, isGoogle: boolean, providerToken: string | null = null) {
        if (!userId) return;

        const now = new Date();
        const currentWeek = this.getWeekIdentifier(now);
        const lastProcessedWeek = localStorage.getItem(RHINO_WEEK_KEY);

        // Already processed this week?
        if (lastProcessedWeek === currentWeek) {
            console.log('RhinoService: Already processed for:', currentWeek);
            return;
        }

        // Check window
        if (!this.isWithinWindow(now)) return;

        if (!isGoogle) {
            throw new Error('NON_GOOGLE_ACCOUNT');
        }

        console.log('RhinoService: Checking for Rhino email...');

        // Fetch and parse email
        const emailData = await fetchRealRhinoEmailData(providerToken);

        if (emailData) {
            await this.applyRhinoData(userId, emailData, currentWeek);
        } else {
            console.log('RhinoService: No Rhino email found yet.');
        }
    },

    isWithinWindow(date: Date): boolean {
        const day = date.getDay();
        const hour = date.getHours();
        // Monday 10AM+ or Tuesday before 5AM
        return (day === 1 && hour >= 10) || (day === 2 && hour < 5);
    },

    getWeekIdentifier(date: Date): string {
        const year = date.getFullYear();
        const oneJan = new Date(year, 0, 1);
        const numberOfDays = Math.floor((date.getTime() - oneJan.getTime()) / (24 * 60 * 60 * 1000));
        const week = Math.ceil((date.getDay() + 1 + numberOfDays) / 7);
        return `${year}-W${week}`;
    },

    async applyRhinoData(userId: string, emailData: RhinoEmailData, weekId: string) {
        const { score, tier, events } = emailData;
        console.log('RhinoService: Score:', score, 'Tier:', tier, 'Events:', events.length);

        let xpReward = 10;
        let message = '';

        if (score >= 95) {
            xpReward = 300;
            message = `Score ${score} - Excelente!`;
        } else if (score >= 90) {
            xpReward = 200;
            message = `Score ${score} - Bom!`;
        } else if (score >= 80) {
            xpReward = 100;
            message = `Score ${score} - OK`;
        }

        // Calculate XP
        const { data: profile } = await supabase.from('profiles').select('xp').eq('id', userId).single();
        const currentXP = profile?.xp || 0;
        const newXP = currentXP + xpReward;

        // Save to DB
        try {
            await supabase.from('profiles').update({
                xp: newXP,
                rhino_score: score,
                rhino_tier: tier,
                rhino_events: JSON.stringify(events),
                rhino_last_update: new Date().toISOString(),
                rhino_week_processed: weekId
            }).eq('id', userId);
        } catch (err) {
            console.warn('RhinoService: Save failed', err);
            await supabase.from('profiles').update({ xp: newXP }).eq('id', userId);
        }

        localStorage.setItem(RHINO_WEEK_KEY, weekId);
        localStorage.setItem(RHINO_CHECK_KEY, new Date().toISOString());

        alert(`🎉 ${message}\n+${xpReward} XP`);
    }
};